import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { ILMSPageHeader } from '../../components/ILMSPageHeader';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSTable } from '../../components/ILMSTable';
import { ILMSFilterBar } from '../../components/ILMSFilterBar';
import { ILMSModal } from '../../components/ILMSModal';
import { ILMSAlert } from '../../components/ILMSAlert';
import { DEFAULT_PAGE_SIZE, ILMSPagination } from '../../components/ILMSPagination';
import { sortRows, type TableSort } from '../../utils/tableSort';
import { getAppearance } from '../../utils/appearance';
import { cn } from '../../utils/cn';
import { GenericForm } from './GenericForm';
import { ExportExcel } from './ExportExcel';
import { applyFormRule, useFormRule } from './formSettings';
import { useReferenceData } from './referenceData';
import type { ModuleConfig } from './types';
import { getApiErrorMessage } from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuth } from '../../hooks/useAuth';

/** Every record of a list, a page of 2000 at a time - for the Excel export. */
async function fetchAllRows<T extends object>(config: ModuleConfig<T>): Promise<T[]> {
  const rows: T[] = [];
  for (let page = 1; page <= 50; page++) {
    const res = await config.api.list({ page, page_size: 2000 });
    rows.push(...res.items);
    if (rows.length >= res.total || res.items.length === 0) break;
  }
  return rows;
}

// The date the Excel date range filters on: the config's own choice, else
// its first required date field (Calving Date, Sale Date...). Lists with
// no such date (masters like Breeds or Suppliers) export in full.
function exportDateOf<T extends object>(config: ModuleConfig<T>): ((row: T) => string | null | undefined) | undefined {
  if (config.exportDateKey === null) return undefined;
  const key = config.exportDateKey ?? config.fields.find((f) => f.type === 'date' && f.required)?.name;
  if (!key) return undefined;
  return (row) => {
    const v = (row as Record<string, unknown>)[key];
    return typeof v === 'string' ? v : undefined;
  };
}

export function ResourceModule<T extends object>({ config: baseConfig, autoOpenAdd }: { config: ModuleConfig<T>; autoOpenAdd?: Record<string, unknown> }) {
  // Required / hidden / label / starting value as set on Administration ->
  // Entry Form Settings.
  const rule = useFormRule(baseConfig.queryKey);
  const config = React.useMemo(() => applyFormRule(baseConfig, rule), [baseConfig, rule]);
  const queryClient = useQueryClient();
  const { can } = useAuth();
  // Gates what the UI even offers to someone, rather than rendering a
  // fully editable Add/Save/Delete experience for everyone with View
  // access and leaving the backend's 403 as the only real stop (added on
  // request, after an Operator could open a Save-able, Delete-able form
  // for an already-Approved milk session they had no Edit/Delete rights
  // to touch in the first place).
  const canCreate = can(config.module, 'Create');
  const canEditPermission = can(config.module, 'Edit');
  const canDeletePermission = can(config.module, 'Delete/Archive');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  // A sorted list is sorted across every record, not just the page on
  // screen: it loads them all (up to the server's 2000 cap) and pages here.
  const [sort, setSort] = useState<TableSort | null>(null);
  const compact = getAppearance().table_density !== 'comfortable';
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<T | null>(null);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Viewing (not creating) a record the signed-in user lacks Edit rights
  // for — same row-click/modal as a normal edit, just with every field and
  // the Save button disabled, since they could already see this via View.
  const isReadOnlyView = !!editing && !canEditPermission;

  const { data: refData, isLoading: refLoading } = useReferenceData(config.referenceKeys ?? []);

  const listQuery = useQuery({
    queryKey: sort ? [config.queryKey, 'all', debouncedSearch] : [config.queryKey, page, pageSize, debouncedSearch],
    queryFn: () => (sort
      ? config.api.list({ page: 1, page_size: 2000, search: debouncedSearch || undefined })
      : config.api.list({ page, page_size: pageSize, search: debouncedSearch || undefined })),
  });

  const openAdd = (prefill?: Record<string, unknown>) => {
    setEditing(null);
    const defaults = typeof config.defaultValues === 'function' ? config.defaultValues() : config.defaultValues;
    setValues({ ...(defaults ?? {}), ...prefill } as unknown as Record<string, unknown>);
    setFieldErrors({});
    setFormError(null);
    setConfirmDelete(false);
    setModalOpen(true);
  };

  // Coming back from a linked screen (e.g. Add Animal, reached via this
  // module's own "+ Add new animal" action) with a value ready to resume
  // this form on — open straight into Add with it pre-filled, once, instead
  // of leaving the user to redo the picker.
  React.useEffect(() => {
    if (autoOpenAdd) openAdd(autoOpenAdd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (row: T) => {
    setEditing(row);
    setValues({ ...row, ...(config.deriveEditValues?.(row) ?? {}) } as unknown as Record<string, unknown>);
    setFieldErrors({});
    setFormError(null);
    setConfirmDelete(false);
    setModalOpen(true);
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (editing) {
        return config.api.update(String(editing[config.idKey]), values as unknown as Partial<T>);
      }
      return config.api.create(values as unknown as Partial<T>);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [config.queryKey] });
      // Dropdowns elsewhere (Animals List filters, Add Animal's Breed/Location
      // selects, etc.) read from a separately-cached useReferenceData(), keyed
      // ['reference', ...] — without this, a newly added Breed/Animal Type/
      // Location wouldn't show up anywhere else until that cache's 5-minute
      // staleTime expired or the page was reloaded.
      queryClient.invalidateQueries({ queryKey: ['reference'] });
      setModalOpen(false);
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 422) {
        const detail = err.response.data?.detail;
        if (Array.isArray(detail)) {
          const nextErrors: Record<string, string> = {};
          detail.forEach((d: { loc?: string[]; msg?: string }) => {
            const field = d.loc?.[d.loc.length - 1];
            if (field) nextErrors[field] = d.msg ?? 'This value is not valid.';
          });
          setFieldErrors(nextErrors);
        }
      }
      setFormError(getApiErrorMessage(err, `Unable to save this ${config.entityLabel}. Please try again.`));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => config.api.remove!(String(editing![config.idKey])),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [config.queryKey] });
      queryClient.invalidateQueries({ queryKey: ['reference'] });
      setModalOpen(false);
      setConfirmDelete(false);
    },
    onError: (err) => setFormError(getApiErrorMessage(err, `Unable to delete this ${config.entityLabel}. Please try again.`)),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isReadOnlyView) return; // defensive — the Save button is hidden, but Enter-to-submit still reaches here
    const nextErrors: Record<string, string> = {};
    config.fields.forEach((f) => {
      const isRequired = typeof f.required === 'function' ? f.required(values) : f.required;
      if (isRequired && f.type !== 'display') {
        const v = values[f.name];
        if (v === undefined || v === null || v === '') {
          nextErrors[f.name] = `${f.label} is required.`;
        }
      }
    });
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setFormError('Please fix the highlighted fields before saving.');
      return;
    }
    setFormError(null);
    mutation.mutate();
  };

  const loaded = listQuery.data?.items ?? [];
  const sorted = React.useMemo(() => (sort ? sortRows(loaded, sort, config.columns) : loaded), [loaded, sort, config.columns]);
  const items = sort ? sorted.slice((page - 1) * pageSize, page * pageSize) : loaded;
  const total = sort ? sorted.length : listQuery.data?.total ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <ILMSPageHeader
        title={config.title}
        subtitle={config.subtitle}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {config.headerExtra}
            <ExportExcel title={config.title} columns={config.columns} fetchRows={() => fetchAllRows(config)} dateOf={exportDateOf(config)} />
            {!config.readOnly && canCreate && (
              <ILMSButton icon={<Plus />} onClick={() => openAdd()}>
                {config.addLabel ?? `Add ${config.entityLabel}`}
              </ILMSButton>
            )}
          </div>
        }
      />

      <ILMSCard padded={false}>
        <div className={cn('border-b border-ink-300/10', compact ? 'p-3' : 'p-4 sm:p-5')}>
          <ILMSFilterBar
            searchValue={search}
            onSearchChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            searchPlaceholder={config.searchPlaceholder ?? `Search ${config.entityLabel}s…`}
          />
        </div>
        <div className={compact ? 'px-3 pt-1 pb-3' : 'p-4 sm:p-5'}>
          <ILMSTable
            columns={config.columns}
            data={items}
            rowKey={(row) => String(row[config.idKey])}
            onRowClick={config.readOnly ? undefined : openEdit}
            isLoading={listQuery.isLoading}
            error={listQuery.isError ? getApiErrorMessage(listQuery.error) : null}
            onRetry={() => listQuery.refetch()}
            sort={sort}
            onSortChange={(s) => { setSort(s); setPage(1); }}
            emptyTitle={`No ${config.entityLabel} records found.`}
            emptyDescription={
              debouncedSearch
                ? 'Try a different search term.'
                : `Get started by adding your first ${config.entityLabel} record.`
            }
            emptyActionLabel={!config.readOnly && canCreate && !debouncedSearch ? `Add ${config.entityLabel}` : undefined}
            onEmptyAction={!config.readOnly && canCreate && !debouncedSearch ? () => openAdd() : undefined}
          />
          {!listQuery.isLoading && !listQuery.isError && total > 0 && (
            <ILMSPagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            />
          )}
        </div>
      </ILMSCard>

      <ILMSModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? (isReadOnlyView ? `View ${config.entityLabel}` : `Edit ${config.entityLabel}`) : `Add ${config.entityLabel}`}
        size="lg"
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {editing && config.canDelete && config.api.remove && canDeletePermission ? (
              confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-secondary text-critical-600">Delete this {config.entityLabel}?</span>
                  <ILMSButton variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>No</ILMSButton>
                  <ILMSButton variant="danger" size="sm" loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
                    Yes, delete
                  </ILMSButton>
                </div>
              ) : (
                <ILMSButton variant="danger" size="sm" icon={<Trash2 />} onClick={() => setConfirmDelete(true)}>
                  Delete
                </ILMSButton>
              )
            ) : <span />}
            <div className="flex items-center gap-2">
              <ILMSButton variant="ghost" onClick={() => setModalOpen(false)}>
                {isReadOnlyView ? 'Close' : 'Cancel'}
              </ILMSButton>
              {!isReadOnlyView && (
                <ILMSButton onClick={handleSubmit} loading={mutation.isPending}>
                  {editing ? 'Save changes' : `Add ${config.entityLabel}`}
                </ILMSButton>
              )}
            </div>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {formError && <ILMSAlert tone="critical">{formError}</ILMSAlert>}
          {isReadOnlyView && (
            <ILMSAlert tone="info">You have view-only access to this {config.entityLabel} — editing it isn't available for your role.</ILMSAlert>
          )}
          {refLoading && (config.referenceKeys?.length ?? 0) > 0 ? (
            <p className="text-secondary text-ink-500">Loading form options…</p>
          ) : (
            <fieldset disabled={isReadOnlyView} className="m-0 flex min-w-0 flex-col gap-4 border-0 p-0">
              <GenericForm
                fields={config.fields}
                sectionColumns={config.sectionColumns}
                values={values}
                onChange={(name, value) => {
                  const field = config.fields.find((f) => f.name === name);
                  setValues((prev) => {
                    const next = { ...prev, [name]: value };
                    const extra = field?.onValueChange?.(value, next);
                    return extra ? { ...next, ...extra } : next;
                  });
                }}
                refData={refData}
                errors={fieldErrors}
              />
            </fieldset>
          )}
        </form>
      </ILMSModal>
    </div>
  );
}
